<template>
  <div class="contract-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('contractDetail.page.title')" :subtitle="$t('contractDetail.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="$router.go(-1)">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-printer" @click="_printContract">
          {{ $t('common.print') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) 合同信息面板 -->
    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('contractDetail.title') }}</span>
        <ds-status-tag :label="contractDetailInfo.stateName || '-'" :text="contractDetailInfo.stateName" type="info" />
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.contractName') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.contractName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.contractCode') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.contractCode || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.contractType') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.contractTypeName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.partyA') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.partyA || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.aContacts') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.aContacts || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.aLink') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.aLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.partyB') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.partyB || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.bContacts') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.bContacts || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.bLink') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.bLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.operator') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.operator || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.operatorLink') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.operatorLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.amount') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatMoney(contractDetailInfo.amount) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.startTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.startTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.endTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.endTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.signingTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.signingTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractDetail.status') }}</span>
          <span class="ds-info-grid__value">
            <ds-status-tag :label="contractDetailInfo.stateName || '-'" :text="contractDetailInfo.stateName" type="info" />
          </span>
        </div>
        <div v-if="contractDetailInfo.contractParentId" class="ds-info-grid__item ds-info-grid__item--wide">
          <span class="ds-info-grid__label">{{ $t('contractDetail.parentContract') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.contractParentName }}({{ contractDetailInfo.contractParentCode }})</span>
        </div>
      </div>
    </div>

    <!-- (3) 业务标签页面板 -->
    <div class="ds-panel ds-panel--tabs">
      <el-tabs v-model="contractDetailInfo._currentTab" @tab-click="changeTab(contractDetailInfo._currentTab)">
        <el-tab-pane :label="$t('contractDetail.room')" name="contractDetailRoom" />
        <el-tab-pane :label="$t('contractDetail.file')" name="contractDetailFile" />
        <el-tab-pane :label="$t('contractDetail.fee')" name="contractDetailFee" />
        <el-tab-pane :label="$t('contractDetail.hisFee')" name="contractDetailHisFee" />
        <el-tab-pane :label="$t('contractDetail.roomFee')" name="ownerDetailRoomFee" />
        <el-tab-pane :label="$t('contractDetail.hisRoomFee')" name="contractDetailHisRoomFee" />
        <el-tab-pane :label="$t('contractDetail.ownerInfo')" name="contractDetailOwner" />
        <el-tab-pane :label="$t('contractDetail.changeLog')" name="contractDetailChange" />
        <el-tab-pane :label="$t('contractDetail.draftApproval')" name="contractDetailFlow" />
        <el-tab-pane :label="$t('contractDetail.subContract')" name="contractDetailSub" />
        <el-tab-pane :label="$t('contractDetail.receipt')" name="contractDetailReceipt" />
      </el-tabs>
    </div>

    <component :is="contractDetailInfo._currentTab" :ref="contractDetailInfo._currentTab"
      :contractId="contractDetailInfo.contractId" :contractName="contractDetailInfo.contractName"
      :ownerId="contractDetailInfo.objId" />
  </div>
</template>

<script>
import ContractDetailRoom from '@/components/contract/contractDetailRoom'
import ContractDetailFile from '@/components/contract/contractDetailFile'
import ContractDetailFee from '@/components/contract/contractDetailFee'
import ContractDetailHisFee from '@/components/contract/contractDetailHisFee'
import OwnerDetailRoomFee from '@/components/owner/ownerDetailRoomFee'
import ContractDetailHisRoomFee from '@/components/contract/contractDetailHisRoomFee'
import ContractDetailChange from '@/components/contract/contractDetailChange'
import ContractDetailFlow from '@/components/contract/contractDetailFlow'
import ContractDetailSub from '@/components/contract/contractDetailSub'
import ContractDetailOwner from '@/components/contract/contractDetailOwner'
import ContractDetailReceipt from '@/components/contract/contractDetailReceipt'
import { queryContract } from '@/api/contract/contractDetailApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ContractDetail',
  components: {
    ContractDetailRoom,
    ContractDetailFile,
    ContractDetailFee,
    ContractDetailHisFee,
    OwnerDetailRoomFee,
    ContractDetailHisRoomFee,
    ContractDetailChange,
    ContractDetailFlow,
    ContractDetailSub,
    ContractDetailOwner,
    ContractDetailReceipt,
    DsPageHeader,
    DsStatusTag
  },
  data() {
    return {
      contractDetailInfo: {
        contractId: '',
        contractName: '',
        contractCode: '',
        contractType: '',
        contractTypeName: '',
        partyA: '',
        partyB: '',
        aContacts: '',
        bContacts: '',
        aLink: '',
        bLink: '',
        operator: '',
        operatorLink: '',
        amount: '',
        startTime: '',
        endTime: '',
        signingTime: '',
        param: '',
        planType: '',
        stateName: '',
        contractParentId: '',
        contractParentName: '',
        contractParentCode: '',
        objId: '',
        files: [],
        _currentTab: 'contractDetailRoom'
      }
    }
  },
  created() {
    this.contractDetailInfo.contractId = this.$route.query.contractId
    if (!this.contractDetailInfo.contractId) {
      return
    }
    const currentTab = this.$route.query.currentTab
    if (currentTab) {
      this.contractDetailInfo._currentTab = currentTab
    }
    this._loadContractInfo()
  },
  methods: {
    formatMoney(value) {
      return formatMoney(value)
    },
    async _loadContractInfo() {
      try {
        const res = await queryContract({
          page: 1,
          row: 1,
          contractId: this.contractDetailInfo.contractId
        })
        const contract = res.data[0]
        Object.assign(this.contractDetailInfo, contract)
        this.changeTab(this.contractDetailInfo._currentTab)
      } catch (error) {
        console.error('Failed to load contract info:', error)
      }
    },
    changeTab(tab) {
      this.contractDetailInfo._currentTab = tab
      setTimeout(() => {
        if (this.$refs[tab] && this.$refs[tab].open) {
          this.$refs[tab].open({
            contractId: this.contractDetailInfo.contractId,
            contractName: this.contractDetailInfo.contractName,
            ownerId: this.contractDetailInfo.objId
          })
        }
      }, 500)
    },
    _printContract() {
      const contract = this.contractDetailInfo
      window.open(`/#/views/contract/printContract?contractTypeId=${contract.contractType}&contractId=${contract.contractId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &--tabs {
    padding: 0 var(--ds-spacing-lg);
  }
}

.ds-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--ds-spacing-md);
  margin-bottom: var(--ds-spacing-lg);
  border-bottom: 1px solid var(--ds-gray-200);
}

.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

/* 信息栅格 */
.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);

  &__item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
  }

  &__value {
    font-size: var(--ds-font-size-md);
    color: var(--ds-gray-800);
    word-break: break-all;
  }
}
</style>
